"use client";

import type { Category, Product } from "@graphql";
import { Typography, cn, getThumbnail } from "@tosize/ui";
import Image from "next/image";
import { NavigationMenu } from "radix-ui";
import type { CSSProperties, ComponentProps } from "react";
import { Link } from "~/components/Link";
import type { Image as ImageType } from "~/graphql/genql/pim";
import { useQuery } from "~/hooks/useQuery";

type Variant = "nav" | "category" | "page" | "product" | "alternative";

type TaxonomyVariantProps =
	| {
			variant: "nav" | "category";
			item: { image?: string | null; name?: string | null; url?: string | null };
	  }
	| {
			variant: "page";
			item: { url?: string; title?: string; image: ImageType };
	  }
	| {
			variant: "product" | "alternative";
			item: {
				imageDetail?: string | null;
				imageBase?: string | null;
				name?: string | null;
				url?: string | null;
			};
	  };

export type TaxonomyCardProps = TaxonomyVariantProps & {
	lang: string;
	className?: ComponentProps<"a">["className"];
	withUTM?: boolean;
};

export function TaxonomyCard(props: TaxonomyCardProps) {
	const { lang, item, variant, className, withUTM = false } = props;
	const defaultClassName =
		"group flex w-full cursor-pointer flex-col overflow-hidden rounded-xl bg-white leading-tight text-primary-medium hover:underline focus-visible:underline focus:outline-none";

	const { createUrl } = useQuery();

	function makeUrl(url?: string, content?: string) {
		return withUTM
			? createUrl(
					{
						utm_source: "categorypage",
						utm_medium: "taxonomy",
						utm_campaign: "crs",
						utm_id: variant,
						utm_content: content ?? null,
					},
					url,
				)
			: url;
	}

	if (variant === "nav") {
		const menuItem = item;
		if (!(menuItem.image && menuItem.name && menuItem.url)) return null;

		return (
			<NavigationMenu.Link
				title={menuItem.name}
				className={cn(defaultClassName, className, "max-w-[calc(310px/1.5)]")}
				asChild
			>
				<Link href={makeUrl(menuItem.url, menuItem.name)}>
					<Content image={menuItem.image} name={menuItem.name} variant={variant} />
				</Link>
			</NavigationMenu.Link>
		);
	}

	if (variant === "product") {
		if (!(item.imageBase && item.name && item.url)) return null;

		return (
			<Link
				href={makeUrl(item.url, item.name)}
				title={item.name}
				className={cn(defaultClassName, className, "max-w-[calc(310px/1.375)]")}
			>
				<Content image={item.imageBase} name={item.name} variant={variant} />
			</Link>
		);
	}

	if (variant === "alternative") {
		const product = item as Product;
		if (!(product.imageBase && product.name)) return null;

		return (
			<Link
				href={makeUrl(product.url, product.name)}
				title={product.name}
				className={cn(defaultClassName, className, "max-w-[calc(310px/1.6)]")}
			>
				<Content
					image={product.imageBase}
					imageDetail={product.imageDetail}
					name={product.name}
					variant={variant}
				/>
			</Link>
		);
	}

	if (variant === "category") {
		const category = item as Category;
		if (!(category.image && category.name)) return null;

		return (
			<Link
				href={makeUrl(category.url, category.name)}
				title={category.name}
				className={cn(defaultClassName, className, "max-w-[calc(310px/1.375)]")}
			>
				<Content image={category.image} name={category.name} variant={variant} />
			</Link>
		);
	}

	if (variant === "page") {
		if (!(item.image && item.title && item.url)) return null;

		return (
			<NavigationMenu.Link
				title={item.title}
				className={cn(defaultClassName, className, "max-w-[calc(310px/1.5)]")}
				asChild
			>
				<Link href={makeUrl(item.url, item.title)}>
					<Content
						image={getThumbnail(item.image, lang, "default")}
						objectFit="cover"
						name={item.title}
						variant={variant}
					/>
				</Link>
			</NavigationMenu.Link>
		);
	}

	return null;
}

interface ContentProps {
	image: string;
	imageDetail?: string;
	name: string;
	variant: Variant;
	objectFit?: CSSProperties["objectFit"];
}

function Content({ image, imageDetail, name, variant, objectFit = "contain" }: ContentProps) {
	if (typeof image !== "string") {
		return null;
	}

	return (
		<>
			<div className="relative flex aspect-[2/1]">
				<Image
					src={image}
					width={620}
					height={310}
					alt={name}
					priority={variant !== "alternative"}
					className="aspect-[2/1]"
					style={{ objectFit: objectFit ?? "unset" }}
				/>
				{!!imageDetail && (
					<span className="invisible absolute top-0 right-0 bottom-0 left-0 opacity-0 group-hover:visible group-hover:opacity-100 group-focus-visible:visible group-focus-visible:opacity-100 motion-safe:transition-opacity">
						<Image
							src={imageDetail}
							width={620}
							height={310}
							alt={name}
							className="aspect-[2/1]"
							style={{ objectFit: objectFit ?? "unset" }}
						/>
					</span>
				)}
			</div>
			<div className="mt-1 mb-2 flex min-h-[2.5rem] grow items-center justify-center px-4 text-center">
				<Typography
					variant="paragraph-bold"
					component="span"
					color="text-primary-medium"
					size={variant === "alternative" ? "text-sm" : undefined}
				>
					{name}
				</Typography>
			</div>
		</>
	);
}
